import { Injectable, inject } from "@angular/core";
import { CanDeactivateFn } from "@angular/router";
import { SharedConfirmationService } from "@vp/shared/confirmation";
import { Observable } from "rxjs";

/**
 * A routed component that can hold work the user has not saved — a note the
 * composer never posted, an open editor. Implement it and register
 * {@link PendingChangesGuard} on the route.
 */
export interface HasPendingChanges {
  hasPendingChanges(): boolean;
}

/**
 * Backs {@link PendingChangesGuard}. `providedIn: "root"` on purpose: the
 * features that use it are lazy modules, and a guard resolved from the root
 * injector works from any of them without each one re-providing it (the same
 * shape as the tag-manager pending-changes guard).
 */
@Injectable({ providedIn: "root" })
export class PendingChangesGuardService {
  private readonly confirmationDialog = inject(SharedConfirmationService);

  /**
   * Let the navigation through untouched when the component reports nothing
   * pending; otherwise ask, and leave only if the user confirms. A component
   * that does not implement the interface never blocks.
   */
  canDeactivate(component: HasPendingChanges | null): Observable<boolean> | boolean {
    if (typeof component?.hasPendingChanges !== "function" || !component.hasPendingChanges()) {
      return true;
    }
    return this.confirmationDialog
      .open(
        "You have unsaved changes. They will be lost if you continue.",
        "Continue",
        "Pending Changes",
        undefined,
        "30vw",
        true,
        true,
        "warn"
      )
      .afterClosed();
  }
}

/**
 * `canDeactivate` guard that confirms before leaving a component with unsaved
 * work:
 *
 *   { path: "case/:caseId", component: …, canDeactivate: [PendingChangesGuard] }
 */
export const PendingChangesGuard: CanDeactivateFn<HasPendingChanges> = (
  component: HasPendingChanges
): Observable<boolean> | boolean => inject(PendingChangesGuardService).canDeactivate(component);
